import { useMutation, useQueryClient } from "@tanstack/react-query";
import { homeApi, HOME_QK, type PinnedTiles, type TileKind } from "../../api/homeApi";
import { QK, summitOpsApi } from "../../api/summitOpsApi";
import { SHOPFLOOR_QK, shopFloorApi } from "../../api/shopFloorApi";
import { PURCHASING_WORKSPACE_QK, purchasingWorkspaceApi, type PurchasingWorkspaceQueue } from "../../api/purchasingWorkspaceApi";
import type { HubKpi } from "../../api/types";
import { useApiQuery } from "../../hooks/useApiQuery";
import type { KpiSource } from "./homeModel";

// Release 1.5.0 Home data: the person's start page, tiles and saved filters (their own, from /home/*), and the figures the
// start views and KPI tiles show — read from the EXISTING endpoints with their existing query keys, so a page already
// open shares the same answer and no figure is computed here.

export function useStartPage(enabled = true) {
  return useApiQuery([...HOME_QK.start], homeApi.getStart, { enabled, staleTime: 60_000 });
}

export function usePinnedTiles(enabled = true) {
  return useApiQuery([...HOME_QK.tiles], homeApi.getTiles, { enabled, staleTime: 30_000 });
}

export function useSavedFilters(screen: string | undefined, enabled = true) {
  return useApiQuery([...HOME_QK.filters(screen)], () => homeApi.getFilters(screen), { enabled, staleTime: 30_000 });
}

/** Pin and unpin from anywhere; the tile list refreshes on Home and in the page tools. */
export function usePinMutations() {
  const queryClient = useQueryClient();
  const settle = (data?: PinnedTiles) => {
    if (data) queryClient.setQueryData([...HOME_QK.tiles], data);
    void queryClient.invalidateQueries({ queryKey: [...HOME_QK.filtersRoot] });
  };
  const pin = useMutation({
    mutationFn: (v: { kind: TileKind; target: string; label?: string }) => homeApi.pin(v.kind, v.target, v.label),
    onSuccess: settle
  });
  const unpin = useMutation({ mutationFn: (id: number) => homeApi.unpin(id), onSuccess: settle });
  return { pin, unpin };
}

/** The four purchasing-queue counts a start card or tile may show, as the queue reports them (no new calculation). */
const PURCHASING_COUNTS: { id: string; label: string; field: keyof PurchasingWorkspaceQueue["counts"]; tone: HubKpi["tone"] }[] = [
  { id: "needsAttentionCount", label: "Needs attention", field: "needsAttentionCount", tone: "warning" },
  { id: "newCount", label: "New routers", field: "newCount", tone: "neutral" },
  { id: "inReviewCount", label: "In review", field: "inReviewCount", tone: "neutral" },
  { id: "approvedCount", label: "Approved, not printed", field: "approvedCount", tone: "good" },
  { id: "returnedCount", label: "Returned", field: "returnedCount", tone: "warning" },
  { id: "changedSinceApprovalCount", label: "Changed since approval", field: "changedSinceApprovalCount", tone: "warning" }
];

export function purchasingCards(queue: PurchasingWorkspaceQueue): HubKpi[] {
  return PURCHASING_COUNTS.map((c) => ({
    id: c.id,
    label: c.label,
    value: queue.counts[c.field] as number,
    valueKind: queue.counts.valueKind,
    unit: null,
    basis: "As counted by the Purchasing queue.",
    drillTo: "/purchasing",
    tone: c.tone
  }));
}

export interface KpiSourceResult {
  kpis: HubKpi[] | undefined;
  isPending: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
}

/** The KPI cards one existing endpoint returns. Only the asked-for source is fetched. */
export function useKpiSource(source: KpiSource | null, scope: string | null = null): KpiSourceResult {
  const hub = useApiQuery([...QK.operationsHub], summitOpsApi.getOperationsHub, { enabled: source === "hub" });
  const hotlist = useApiQuery([...SHOPFLOOR_QK.hotlist], shopFloorApi.getHotlist, { enabled: source === "hotlist" });
  const dispatch = useApiQuery([...SHOPFLOOR_QK.dispatch(scope ?? undefined)], () => shopFloorApi.getDispatchBoard(scope ? { shop: scope } : undefined), {
    enabled: source === "dispatch"
  });
  const queue = useApiQuery([...PURCHASING_WORKSPACE_QK.queue], purchasingWorkspaceApi.getQueue, { enabled: source === "purchasing-queue" });
  const q = source === "hub" ? hub : source === "hotlist" ? hotlist : source === "dispatch" ? dispatch : source === "purchasing-queue" ? queue : null;
  if (!q) return { kpis: undefined, isPending: false, isError: false, error: null, refetch: () => undefined };
  const kpis =
    source === "purchasing-queue"
      ? queue.data
        ? purchasingCards(queue.data)
        : undefined
      : (q.data as { kpis?: HubKpi[] } | undefined)?.kpis;
  return { kpis, isPending: q.isPending, isError: q.isError, error: q.error, refetch: () => void q.refetch() };
}
